/**
 * Optimal Bidding Table Utility Functions
 * Handles data processing and calculations for optimal bidding strategies
 */

// Mock data generation for optimal bidding table
export const generateOptimalBiddingData = (count = 12) => {
  const strategies = [
    'Aggressive Peak', 'Conservative Base', 'Balanced Mix', 'Market Following',
    'Risk Averse', 'Opportunistic', 'Steady State', 'Dynamic Pricing',
    'Volume Focused', 'Margin Focused', 'Hybrid Approach', 'Seasonal Adjust'
  ];
  
  const statuses = ['Active', 'Pending', 'Inactive'];
  const categories = ['High Risk', 'Medium Risk', 'Low Risk'];
  
  return Array.from({ length: count }, (_, index) => ({
    id: index + 1,
    strategy: strategies[index % strategies.length],
    category: categories[index % categories.length],
    bidAmount: Math.round(Math.random() * 500000 + 100000),
    expectedProfit: Math.round((Math.random() - 0.3) * 100000),
    riskScore: Math.round(Math.random() * 100),
    probability: Math.round(Math.random() * 100),
    volume: Math.round(Math.random() * 1000 + 100),
    efficiency: Math.round(Math.random() * 40 + 60),
    status: statuses[Math.floor(Math.random() * statuses.length)]
  }));
};

// Calculate risk level based on thresholds
export const getRiskLevel = (row, thresholds) => {
  const profit = parseFloat(row.expectedProfit) || 0;
  
  if (!thresholds) return 'medium'; // Default if thresholds not available
  
  if (profit >= thresholds.high) return 'low';
  if (profit >= thresholds.medium) return 'medium';
  return 'high';
};

// Calculate bidding metrics
export const calculateBiddingMetrics = (data, riskLevel) => {
  if (!data || !Array.isArray(data)) return null;
  
  const filteredData = riskLevel === 'All' 
    ? data 
    : data.filter(item => item.category === riskLevel);
  
  if (filteredData.length === 0) return null;
  
  const totalBidAmount = filteredData.reduce((sum, item) => sum + (item.bidAmount || 0), 0);
  const totalProfit = filteredData.reduce((sum, item) => sum + (item.expectedProfit || 0), 0);
  const avgRiskScore = filteredData.reduce((sum, item) => sum + (item.riskScore || 0), 0) / filteredData.length;
  const avgProbability = filteredData.reduce((sum, item) => sum + (item.probability || 0), 0) / filteredData.length;
  const avgEfficiency = filteredData.reduce((sum, item) => sum + (item.efficiency || 0), 0) / filteredData.length;
  
  return {
    totalBidAmount,
    totalProfit,
    avgRiskScore: Math.round(avgRiskScore),
    avgProbability: Math.round(avgProbability),
    avgEfficiency: Math.round(avgEfficiency),
    count: filteredData.length,
    profitMargin: totalBidAmount > 0 ? (totalProfit / totalBidAmount * 100) : 0
  };
};

// Format currency values
export const formatCurrency = (value, options = {}) => {
  const {
    currency = 'USD',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = 'en-US'
  } = options;
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value || 0);
};

// Format percentage values
export const formatPercentage = (value, decimals = 1) => {
  return `${(value || 0).toFixed(decimals)}%`;
};

// Get value class for styling
export const getValueClass = (value, type = 'neutral') => {
  const numValue = parseFloat(value) || 0;
  
  if (type === 'profit' || type === 'percentage') {
    if (numValue > 0) return 'positive';
    if (numValue < 0) return 'negative';
  }
  
  return 'neutral';
};

// Validate bidding data
export const validateBiddingData = (data, columns) => {
  const errors = [];
  
  if (!data || !Array.isArray(data)) {
    errors.push('Data must be an array');
    return { isValid: false, errors };
  }
  
  if (data.length === 0) {
    errors.push('Data array cannot be empty');
    return { isValid: false, errors };
  }
  
  // Check required columns
  const requiredColumns = columns.filter(col => col.required !== false).map(col => col.key);
  
  data.forEach((row, index) => {
    requiredColumns.forEach(column => {
      if (row[column] === undefined || row[column] === null) {
        errors.push(`Missing required field '${column}' in row ${index + 1}`);
      }
    });
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Sort data by column
export const sortBiddingData = (data, sortBy, sortOrder = 'asc') => {
  if (!data || !Array.isArray(data)) return [];
  
  return [...data].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Convert to numbers if possible
    if (!isNaN(aVal) && !isNaN(bVal)) {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
};

// Filter data by criteria
export const filterBiddingData = (data, filters) => {
  if (!data || !Array.isArray(data)) return [];
  if (!filters || Object.keys(filters).length === 0) return data;
  
  return data.filter(row => {
    return Object.entries(filters).every(([key, value]) => {
      if (value === 'All' || value === '' || value === null) return true;
      return row[key] === value;
    });
  });
};

export default {
  generateOptimalBiddingData,
  getRiskLevel,
  calculateBiddingMetrics,
  formatCurrency,
  formatPercentage,
  getValueClass,
  validateBiddingData,
  sortBiddingData,
  filterBiddingData
};
